import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CallEntity } from './call.entity';
import { UserEntity } from './user.entity';

/**
 * Why this exists: a single call can produce **multiple recordings**
 * when the rep pauses + resumes mid-call (PCI capture, sensitive-info
 * moments, or two-party-state withdrawal-of-consent). Twilio's pause
 * API can collapse pauses into one Sid with silence, but the
 * documented Approach B (see `10-compliance.md §2 "Mid-call recording
 * pause / resume"`) keeps each segment as its own Twilio Recording
 * resource — never stitch — so the timeline is fully auditable and
 * any "what was said during the pause" question has a defensible
 * answer ("nothing was captured, by design").
 *
 * One row per segment, ordered by `segmentIndex` (the value of which
 * is computed at insert time and never changes). A simple call with
 * no pauses produces exactly one row; a call with N pauses produces
 * N+1 rows.
 *
 * Lifecycle:
 *   - `in-progress` webhook fires when Twilio starts capturing →
 *     row inserted with `startedAt`, `twilioRecordingSid`, and
 *     `endedAt = null`. (Status callbacks must include `in-progress`
 *     on the TwiML for this to fire — see
 *     `twilio-voice-human-rep.controller.ts:buildAssignedDialTwiml`.)
 *   - Rep clicks Pause OR the call ends naturally → backend calls
 *     `client.calls(callSid).recordings(sid).update({Status:'stopped'})`,
 *     then sets `endedAt`, `endReason`, and (for pauses)
 *     `fkUserIdPausedBy`/`pauseReason`/`pauseNote` synchronously.
 *   - `completed` webhook fires → row updated with
 *     `twilioRecordingUrl`, `durationSeconds`, `channels`. (Twilio
 *     uploads can take 5-30s after the recording ends; the URL is
 *     the indicator that the audio is fetchable.)
 *   - Rep clicks Resume → backend calls
 *     `client.calls(callSid).recordings.create({...})`, inserts a
 *     fresh row with `segmentIndex = previous + 1`.
 *
 * The legacy direct columns on `CallEntity` (`recordingSid`,
 * `recordingUrl`, `recordingDurationSeconds`, `recordingChannels`)
 * stay — they're populated from segment #1 for back-compat with
 * existing playback/recording-access code. The new entity is the
 * source of truth for the FULL segment list; the legacy columns are
 * a "primary segment shortcut."
 */
@Index('call_recording_segment__fk_call_idx', ['fkCallId'], {})
@Index('call_recording_segment__sid_uq', ['twilioRecordingSid'], {
	unique: true,
})
@Entity('call_recording_segment')
export class CallRecordingSegmentEntity extends BaseEntity {
	@Column('int', { name: 'fk_call__id', unsigned: true })
	public fkCallId!: number;

	/**
	 * Ordinal position of this segment within the call's recording
	 * timeline. Starts at 1. Computed by `MAX(segmentIndex)+1` for the
	 * call at insert time (inside a transaction that locks the call
	 * row, so two simultaneous Resume clicks can't collide on the
	 * same index).
	 */
	@Column('int', { name: 'segmentIndex', unsigned: true })
	public segmentIndex!: number;

	/**
	 * Twilio Recording SID (RExxxx…). Unique across all calls in the
	 * tenant — the unique index doubles as the idempotency key for
	 * Twilio's webhook retries.
	 */
	@Column('varchar', { name: 'twilioRecordingSid', length: 64 })
	public twilioRecordingSid!: string;

	/**
	 * Twilio CDN URL. Populated only after the `completed` webhook;
	 * null while the segment is recording or has just been stopped
	 * but Twilio hasn't finished uploading.
	 */
	@Column('varchar', {
		name: 'twilioRecordingUrl',
		nullable: true,
		length: 512,
	})
	public twilioRecordingUrl!: string | null;

	@Column('int', {
		name: 'durationSeconds',
		nullable: true,
		unsigned: true,
	})
	public durationSeconds!: number | null;

	/** 'mono' | 'dual'. Mirrors `CallEntity.recordingChannels`. */
	@Column('varchar', { name: 'channels', nullable: true, length: 8 })
	public channels!: string | null;

	@Column('datetime', { name: 'startedAt' })
	public startedAt!: Date;

	/**
	 * Null while the segment is still recording. Set synchronously by
	 * the pause endpoint (when the rep clicks Pause) OR by the call-
	 * ended cleanup (when Twilio closes the recording because the
	 * call hung up).
	 */
	@Column('datetime', { name: 'endedAt', nullable: true })
	public endedAt!: Date | null;

	/**
	 * Why the recording ended. Surfaces in audit logs and is the
	 * basis for the gap-marker text in the assembled transcript:
	 *   `[--- recording paused at 02:14 for 47s; reason: pci_capture (Alice K.) ---]`
	 *
	 * Values:
	 *   - `call_ended`   — Twilio closed the recording because the
	 *                      `<Dial>` finished (call hangup, transfer-out,
	 *                      etc.). No pause was involved.
	 *   - `paused_by_rep` — rep clicked the Pause button while the
	 *                       call was still in progress.
	 *   - `failed`       — Twilio reported a recording failure (rare;
	 *                      usually a Twilio-internal issue or a
	 *                      cancelled in-progress recording).
	 *   - `absent`       — Twilio fired `absent` status (the recording
	 *                      was scheduled but no audio was captured —
	 *                      e.g. the call didn't connect).
	 */
	@Column('varchar', { name: 'endReason', nullable: true, length: 16 })
	public endReason!:
		| 'call_ended'
		| 'paused_by_rep'
		| 'failed'
		| 'absent'
		| null;

	/**
	 * The rep who clicked Pause. Set only when `endReason ===
	 * 'paused_by_rep'`. `ON DELETE SET NULL` so user-deletion doesn't
	 * cascade and erase the audit trail — the segment row stays,
	 * the attribution just becomes "[deleted user]".
	 */
	@Column('int', {
		name: 'fk_user__id__paused_by',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdPausedBy!: number | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_user__id__paused_by', referencedColumnName: 'id' }])
	public fkUserPausedBy!: UserEntity | null;

	/**
	 * Pause classification chosen by the rep from a fixed-set
	 * dropdown. Drives the gap-marker copy in the assembled transcript
	 * + lets the per-tenant compliance dashboard chart "how often are
	 * reps pausing for PCI vs customer-request vs other." Null when
	 * `endReason !== 'paused_by_rep'`.
	 */
	@Column('varchar', { name: 'pauseReason', nullable: true, length: 32 })
	public pauseReason!:
		| 'pci_capture'
		| 'sensitive_info'
		| 'customer_request'
		| 'other'
		| null;

	/**
	 * Optional free-text the rep can supply alongside `pauseReason`
	 * (typically "captured CC #####1234" or "customer asked"). Goes
	 * into the audit log only — NOT echoed into the gap marker
	 * (transcript shouldn't leak free-text rep notes to the LLM).
	 */
	@Column('text', { name: 'pauseNote', nullable: true })
	public pauseNote!: string | null;

	/**
	 * Per-segment transcript text from Deepgram. Cached on the
	 * segment so the post-call processor can re-assemble the full
	 * transcript without re-transcribing every segment if only some
	 * succeeded the first time. The legacy `CallEntity.transcriptText`
	 * is set to the assembled full transcript (with gap markers); this
	 * field is the per-segment building block.
	 */
	@Column('text', { name: 'transcriptText', nullable: true })
	public transcriptText!: string | null;

	/**
	 * 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped'.
	 * `skipped` means the segment had no usable audio (absent/failed
	 * end reason) — we don't try to transcribe it.
	 */
	@Column('varchar', {
		name: 'transcriptionStatus',
		nullable: true,
		length: 16,
	})
	public transcriptionStatus!: string | null;

	@Column('text', { name: 'transcriptionError', nullable: true })
	public transcriptionError!: string | null;

	@ManyToOne(() => CallEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_call__id', referencedColumnName: 'id' }])
	public fkCall!: CallEntity;
}
